/**
 * WarehouseLayoutDesigner.jsx — iter175
 *
 * Bird's-eye grid warehouse designer.
 *
 * Interaction model (kept purposefully simple — no external DnD lib):
 *   • Palette on the left holds one draggable "template" per tile kind.
 *   • Dragging a palette item over the grid highlights the snapped cell and
 *     drops a fresh tile of that kind (default 3×2) when the mouse is
 *     released. HTML5 drag events carry the kind via dataTransfer.
 *   • Placed tiles are draggable + resizable via a bottom-right handle.
 *     Both operations snap to the grid.
 *   • Right-hand inspector lets the operator rename, resize precisely,
 *     rotate, delete, and (for Rack/Floor) link real location codes.
 *
 * State model:
 *   Everything lives in local React state; a single "Save layout" button
 *   commits to PUT /api/warehouses/{id}/layout. A dirty indicator lets the
 *   operator know when they have unsaved changes.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FloppyDisk, Trash, ArrowsClockwise, MagnifyingGlassPlus, MagnifyingGlassMinus,
  MapPin, Warning, LinkSimple, ArrowsOutCardinal, DotsSix, X, Play, Pause, PlayCircle, Fire, ChartBar, ArrowsSplit, MagicWand, FilePdf, ChartLine, Package, Stack,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import {
  getWarehouses, getTileKinds, getWarehouseLayout, saveWarehouseLayout, listLayoutLocations,
  getAbcOverlay, getLayoutSimulation, simulateOrder, listCompletedPickListsForPlayback,
  exportLayoutPdf, getLayoutLinkSuggestions, generateRackLocations,
} from '../lib/api';
import WarehouseKpiPanel from '../components/WarehouseKpiPanel';
import { AutoLinkDialog } from '../components/AutoLinkDialog';
import { RackStructurePanel } from '../components/layout/RackStructurePanel';
import { LocationContentsDialog } from '../components/layout/LocationContentsDialog';
import { LevelHeatmapDialog } from '../components/layout/LevelHeatmapDialog';

const uid = () => `tl_${Math.random().toString(36).slice(2, 10)}`;
const DEFAULT_SIZES = { rack: [3, 2], floor: [3, 2], production: [6, 4], receiving: [4, 3], dispatch: [4, 3], walkway: [10, 1], forklift: [10, 1], restricted: [4, 4], packing: [3, 2], charging: [2, 2] };

export default function WarehouseLayoutDesigner() {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWh, setSelectedWh] = useState('');
  const [kinds, setKinds] = useState([]);
  const [gridCols, setGridCols] = useState(50);
  const [gridRows, setGridRows] = useState(30);
  const [tiles, setTiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [cell, setCell] = useState(24);         // px per grid cell (zoom)
  const [dragKind, setDragKind] = useState(null); // palette drag
  const [hoverCell, setHoverCell] = useState(null); // {x,y}
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);

  // ---------- iter176: overlays + playback state ----------
  // 'none' | 'abc' | 'congestion'
  const [overlayMode, setOverlayMode] = useState(() => {
    // iter227 — auto-enable ABC overlay when opened via a demo scenario link.
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      if (p.get('abc') === '1') return 'abc';
    }
    return 'none';
  });
  const [abcData, setAbcData] = useState(null);          // ABC overlay payload
  const [simData, setSimData] = useState(null);          // Simulator payload
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Playback of a single pick list route
  const [pickLists, setPickLists] = useState([]);
  const [selectedPickListId, setSelectedPickListId] = useState('');
  const [playback, setPlayback] = useState(null);        // route payload
  const [playbackStep, setPlaybackStep] = useState(0);   // waypoint index
  const [playbackPlaying, setPlaybackPlaying] = useState(false);

  // ---------- iter206: KPI panel state ----------
  const [kpiOpen, setKpiOpen] = useState(false);

  // Move / resize state — kept in refs so mousemove doesn't rerender on every event
  const dragState = useRef(null);
  const svgRef = useRef(null);

  const kindsById = useMemo(() => Object.fromEntries(kinds.map((k) => [k.kind, k])), [kinds]);
  const selectedTile = tiles.find((t) => t.id === selectedId) || null;

  // ---------- Initial load ----------
  useEffect(() => {
    (async () => {
      try {
        const [wh, kd] = await Promise.all([getWarehouses(), getTileKinds()]);
        setKinds(kd.data.kinds || []);
        const list = wh.data || [];
        setWarehouses(list);
        const defaultWh = list.find((w) => w.is_default) || list[0];
        if (defaultWh) setSelectedWh(defaultWh.id);
      } catch (e) {
        toast.error('Failed to load designer');
      }
    })();
  }, []);

  // ---------- Load layout when warehouse changes ----------
  useEffect(() => {
    if (!selectedWh) return;
    (async () => {
      setLoading(true);
      try {
        const r = await getWarehouseLayout(selectedWh);
        setGridCols(r.data.grid_cols);
        setGridRows(r.data.grid_rows);
        setTiles(r.data.tiles || []);
        setSelectedId(null);
        setDirty(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load layout');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedWh]);

  // ---------- Placement / movement helpers ----------
  const clientToCell = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const svgPt = pt.matrixTransform(ctm.inverse());
    return {
      x: Math.max(0, Math.min(gridCols - 1, Math.floor(svgPt.x / cell))),
      y: Math.max(0, Math.min(gridRows - 1, Math.floor(svgPt.y / cell))),
    };
  }, [cell, gridCols, gridRows]);

  const overlapsSolid = (candidate) => {
    const floor = new Set(['walkway', 'forklift', 'restricted']);
    if (floor.has(candidate.kind)) return false;
    return tiles.some((t) => (
      t.id !== candidate.id &&
      !floor.has(t.kind) &&
      candidate.x < t.x + t.w && candidate.x + candidate.w > t.x &&
      candidate.y < t.y + t.h && candidate.y + candidate.h > t.y
    ));
  };

  const placeTileAt = (kind, x, y) => {
    const [w, h] = DEFAULT_SIZES[kind] || [3, 2];
    const nx = Math.max(0, Math.min(gridCols - w, x));
    const ny = Math.max(0, Math.min(gridRows - h, y));
    const candidate = { id: uid(), kind, x: nx, y: ny, w, h, rotation: 0, label: '', location_ids: [] };
    if (overlapsSolid(candidate)) {
      toast.error('That spot overlaps an existing tile.');
      return;
    }
    setTiles((prev) => [...prev, candidate]);
    setSelectedId(candidate.id);
    setDirty(true);
  };

  // ---------- Palette drag ----------
  const onPaletteDragStart = (kind) => (e) => {
    setDragKind(kind);
    e.dataTransfer.setData('text/plain', kind);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const onGridDragOver = (e) => {
    if (!dragKind) return;
    e.preventDefault();
    const cellCoord = clientToCell(e.clientX, e.clientY);
    if (cellCoord) setHoverCell(cellCoord);
  };
  const onGridDrop = (e) => {
    e.preventDefault();
    if (!dragKind) return;
    const cellCoord = clientToCell(e.clientX, e.clientY);
    if (cellCoord) placeTileAt(dragKind, cellCoord.x, cellCoord.y);
    setDragKind(null); setHoverCell(null);
  };

  // ---------- Move existing tile ----------
  const onTileMouseDown = (t, mode) => (e) => {
    e.stopPropagation();
    setSelectedId(t.id);
    const start = clientToCell(e.clientX, e.clientY);
    if (!start) return;
    dragState.current = { id: t.id, mode, start, origin: { x: t.x, y: t.y, w: t.w, h: t.h } };

    const move = (ev) => {
      const cur = clientToCell(ev.clientX, ev.clientY);
      const st = dragState.current;
      if (!cur || !st) return;
      const dx = cur.x - st.start.x;
      const dy = cur.y - st.start.y;
      setTiles((prev) => prev.map((tt) => {
        if (tt.id !== st.id) return tt;
        if (st.mode === 'move') {
          const nx = Math.max(0, Math.min(gridCols - tt.w, st.origin.x + dx));
          const ny = Math.max(0, Math.min(gridRows - tt.h, st.origin.y + dy));
          return { ...tt, x: nx, y: ny };
        }
        // resize (bottom-right handle)
        const nw = Math.max(1, Math.min(gridCols - tt.x, st.origin.w + dx));
        const nh = Math.max(1, Math.min(gridRows - tt.y, st.origin.h + dy));
        return { ...tt, w: nw, h: nh };
      }));
    };
    const up = () => {
      dragState.current = null;
      setDirty(true);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // ---------- Inspector actions ----------
  const updateSelected = (patch) => {
    if (!selectedId) return;
    setTiles((prev) => prev.map((t) => t.id === selectedId ? { ...t, ...patch } : t));
    setDirty(true);
  };
  const deleteSelected = () => {
    if (!selectedId) return;
    setTiles((prev) => prev.filter((t) => t.id !== selectedId));
    setSelectedId(null);
    setDirty(true);
  };
  const rotateSelected = () => {
    if (!selectedTile) return;
    updateSelected({ rotation: (selectedTile.rotation + 90) % 360 });
  };

  // ---------- Rack levels × depth + SKU contents ----------
  const [contentsFor, setContentsFor] = useState(null); // {tile} or {all:true}
  const [levelHeatOpen, setLevelHeatOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const handleGenerateRack = async () => {
    if (!selectedTile || !selectedWh) return;
    setGenerating(true);
    try {
      const t = selectedTile;
      const r = await generateRackLocations(selectedWh, {
        base_code: t.base_code, levels: t.levels || 1, depth: t.depth || 1, zone: t.zone, aisle: t.aisle,
      });
      const elsewhere = new Set(tiles.filter((x) => x.id !== t.id).flatMap((x) => x.location_ids || []));
      const ids = r.data.positions.map((p) => p.id).filter((id) => !elsewhere.has(id));
      const skipped = r.data.positions.length - ids.length;
      updateSelected({ location_ids: [...new Set([...(t.location_ids || []), ...ids])] });
      toast.success(`${r.data.created} new location${r.data.created === 1 ? '' : 's'} created · ${ids.length} linked${skipped ? ` · ${skipped} already on another rack` : ''} — save the layout to keep them.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create locations');
    } finally {
      setGenerating(false);
    }
  };

  // ---------- Auto-link racks by zone + aisle ----------
  const [autoLink, setAutoLink] = useState(null);
  const [autoLinking, setAutoLinking] = useState(false);
  const handleAutoLink = async () => {
    if (!selectedWh) return;
    setAutoLinking(true);
    try {
      const r = await getLayoutLinkSuggestions(selectedWh, tiles);
      setAutoLink(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load suggestions');
    } finally {
      setAutoLinking(false);
    }
  };
  const applyAutoLink = (chosen) => {
    const byTile = Object.fromEntries(chosen.map((s) => [s.tile_id, s.locations.map((l) => l.id)]));
    setTiles((prev) => prev.map((t) => byTile[t.id]
      ? { ...t, location_ids: [...new Set([...(t.location_ids || []), ...byTile[t.id]])] }
      : t));
    setDirty(true);
    setAutoLink(null);
    const n = chosen.reduce((sum, s) => sum + s.locations.length, 0);
    toast.success(`Linked ${n} location${n === 1 ? '' : 's'} to ${chosen.length} rack${chosen.length === 1 ? '' : 's'} — save the layout to keep them.`);
  };

  // ---------- Save ----------
  const handleSave = async () => {
    if (!selectedWh) return;
    setSaving(true);
    try {
      const r = await saveWarehouseLayout(selectedWh, { grid_cols: gridCols, grid_rows: gridRows, tiles });
      setTiles(r.data.tiles || []);
      setDirty(false);
      toast.success('Layout saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Export executive PDF for management sign-off ----------
  const [exportingPdf, setExportingPdf] = useState(false);
  const handleExportPdf = async () => {
    if (!selectedWh) return;
    setExportingPdf(true);
    try {
      const r = await exportLayoutPdf(selectedWh);
      // Trigger a browser download of the returned PDF blob. Prefer the
      // canonical filename from the server's Content-Disposition header
      // (avoids client-side timezone drift on the date stamp).
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      let filename = null;
      const disp = r.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disp);
      if (match && match[1]) filename = match[1];
      if (!filename) {
        const wh = warehouses.find((w) => w.id === selectedWh);
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        filename = `layout-report-${(wh?.code || 'warehouse').toLowerCase().replace(/\s+/g, '-')}-${stamp}.pdf`;
      }
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF ready — sending to management just got easier.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  const width = gridCols * cell;
  const height = gridRows * cell;

  // ---------- iter176: analytics loaders ----------
  // Refetch overlay data whenever the mode + warehouse changes. We keep both
  // datasets in state independently so switching modes is cheap.
  useEffect(() => {
    if (!selectedWh || overlayMode === 'none') return;
    (async () => {
      setAnalyticsLoading(true);
      try {
        if (overlayMode === 'abc' && !abcData) {
          const r = await getAbcOverlay(selectedWh, 90);
          setAbcData(r.data);
        }
        if (overlayMode === 'congestion' && !simData) {
          const r = await getLayoutSimulation(selectedWh, 30);
          setSimData(r.data);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load overlay');
      } finally { setAnalyticsLoading(false); }
    })();
  }, [selectedWh, overlayMode, abcData, simData]);

  // Reset cached analytics when the warehouse changes.
  useEffect(() => {
    setAbcData(null); setSimData(null); setPlayback(null); setSelectedPickListId('');
    setPickLists([]); setPlaybackStep(0); setPlaybackPlaying(false);
  }, [selectedWh]);

  // Lazy-load recent pick lists the first time the operator opens the
  // Playback picker so we don't hammer the API on page load.
  const loadPickLists = async () => {
    if (pickLists.length || !selectedWh) return;
    try {
      const r = await listCompletedPickListsForPlayback(selectedWh, 50);
      const list = Array.isArray(r.data) ? r.data : (r.data?.rows || r.data?.items || []);
      setPickLists(list);
    } catch (e) {
      toast.error('Failed to load pick lists');
    }
  };

  const runPlayback = async (pickListId) => {
    if (!pickListId) return;
    setPlayback(null); setPlaybackStep(0); setPlaybackPlaying(false);
    try {
      const r = await simulateOrder(selectedWh, pickListId);
      setPlayback(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Playback failed');
    }
  };

  // Playback stepper — 900 ms per waypoint. Animation loop stops at end.
  useEffect(() => {
    if (!playbackPlaying || !playback) return;
    if (playbackStep >= playback.route_points.length - 1) {
      setPlaybackPlaying(false);
      return;
    }
    const t = setTimeout(() => setPlaybackStep((s) => s + 1), 900);
    return () => clearTimeout(t);
  }, [playbackPlaying, playbackStep, playback]);

  // Congestion / ABC tint lookups
  const overlayTintFor = (tile) => {
    if (overlayMode === 'abc') {
      const abc = abcData?.tiles?.[tile.id];
      if (!abc) return null;
      return { A: '#dc2626', B: '#f59e0b', C: '#2563eb' }[abc.tier] || null;
    }
    if (overlayMode === 'congestion') {
      const c = simData?.congestion?.[tile.id];
      if (!c) return null;
      return { green: '#16a34a', amber: '#f59e0b', red: '#dc2626' }[c.tier] || null;
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="warehouse-layout-designer">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="overline">Warehouse</p>
            <h1 className="text-2xl font-bold">Layout Designer</h1>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Drop tiles on the grid to recreate your physical warehouse. Racking and floor-stack tiles can be
              linked to real location codes so future simulations know what&apos;s in each cell.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Warehouse</Label>
              <Select value={selectedWh} onValueChange={setSelectedWh}>
                <SelectTrigger className="w-56" data-testid="wh-select">
                  <SelectValue placeholder="Choose a warehouse…" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => setCell((c) => Math.max(10, c - 4))} data-testid="zoom-out-btn"><MagnifyingGlassMinus size={14} /></Button>
              <span className="text-[10px] font-mono">{cell}px</span>
              <Button size="sm" variant="outline" onClick={() => setCell((c) => Math.min(60, c + 4))} data-testid="zoom-in-btn"><MagnifyingGlassPlus size={14} /></Button>
            </div>
            <Button
              onClick={() => setLevelHeatOpen(true)}
              disabled={!selectedWh}
              variant="outline"
              data-testid="level-heatmap-btn"
              title="Which rack levels get picked most, plus swaps to bring fast movers down to the ground"
            >
              <Stack size={14} className="mr-1" /> Level heat-map
            </Button>
            <Button
              onClick={() => setContentsFor({ all: true })}
              disabled={!selectedWh}
              variant="outline"
              data-testid="all-skus-btn"
              title="See every SKU in every location of this warehouse (stock + fixed locations)"
            >
              <Package size={14} className="mr-1" /> All SKUs
            </Button>
            <Button
              onClick={handleAutoLink}
              disabled={!selectedWh || autoLinking}
              variant="outline"
              data-testid="auto-link-racks-btn"
              title="Suggest which location codes belong to each rack, using the zone and aisle set on each tile"
            >
              <MagicWand size={14} className="mr-1" />
              {autoLinking ? 'Matching…' : 'Auto-link racks'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || !selectedWh}
              className="bg-brand hover:bg-brand/90"
              data-testid="save-layout-btn"
            >
              <FloppyDisk size={14} className="mr-1" />
              {saving ? 'Saving…' : dirty ? 'Save layout' : 'Saved'}
            </Button>
            <Button
              onClick={handleExportPdf}
              disabled={!selectedWh || exportingPdf}
              variant="outline"
              data-testid="export-layout-pdf-btn"
              title="Download a 2-page executive PDF for upper management sign-off"
            >
              <FilePdf size={14} className="mr-1" />
              {exportingPdf ? 'Preparing…' : 'Export PDF'}
            </Button>
          </div>
        </div>

        {/* Grid dimensions row */}
        <div className="flex items-center gap-3 text-xs">
          <Label>Grid</Label>
          <Input
            type="number" min={5} max={500} value={gridCols}
            onChange={(e) => { setGridCols(Math.max(5, Math.min(500, Number(e.target.value) || 50))); setDirty(true); }}
            className="w-20" data-testid="grid-cols"
          />
          <span className="text-muted-foreground">columns ×</span>
          <Input
            type="number" min={5} max={500} value={gridRows}
            onChange={(e) => { setGridRows(Math.max(5, Math.min(500, Number(e.target.value) || 30))); setDirty(true); }}
            className="w-20" data-testid="grid-rows"
          />
          <span className="text-muted-foreground">rows · 1 cell ≈ 1 m · {tiles.length} tile{tiles.length === 1 ? '' : 's'}</span>
          {dirty && <Badge variant="outline" className="border-amber-400 text-amber-800">Unsaved changes</Badge>}
        </div>

        {/* iter176: Overlay + Playback toolbar */}
        <div className="flex items-center gap-2 flex-wrap border border-border bg-muted/20 px-3 py-2 rounded" data-testid="overlay-toolbar">
          <span className="overline mr-1">Overlay</span>
          {[
            { key: 'none', label: 'None', icon: null },
            { key: 'abc', label: 'ABC (90d)', icon: ChartBar },
            { key: 'congestion', label: 'Congestion (30d)', icon: Fire },
          ].map((o) => {
            const Icon = o.icon;
            const active = overlayMode === o.key;
            return (
              <Button
                key={o.key} size="sm"
                variant={active ? 'default' : 'outline'}
                className={active ? 'bg-brand hover:bg-brand/90' : ''}
                onClick={() => setOverlayMode(o.key)}
                data-testid={`overlay-btn-${o.key}`}
              >
                {Icon && <Icon size={12} className="mr-1" weight={active ? 'fill' : 'regular'} />} {o.label}
              </Button>
            );
          })}
          {analyticsLoading && <span className="text-[10px] text-muted-foreground">Loading…</span>}
          {overlayMode === 'abc' && abcData && (
            <div className="flex items-center gap-3 text-[10px] ml-3" data-testid="abc-legend">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#dc2626' }} /> A (top 20% picks)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#f59e0b' }} /> B (next 30%)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#2563eb' }} /> C (bottom 50%)</span>
              <span className="text-muted-foreground">· {abcData.total_picks_in_layout || 0} of {abcData.total_picks || 0} picks in linked tiles</span>
            </div>
          )}
          {overlayMode === 'congestion' && simData && (
            <div className="flex items-center gap-3 text-[10px] ml-3" data-testid="congestion-legend">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#dc2626' }} /> Hot (top 3rd)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#f59e0b' }} /> Warm</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: '#16a34a' }} /> Cool</span>
              <span className="text-muted-foreground">· {simData.tickets_analyzed} tickets · {simData.total_distance_m.toLocaleString()} m walked · {simData.avg_distance_per_ticket_m} m/ticket avg</span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2" data-testid="playback-controls">
            <span className="overline">Playback</span>
            <Select
              value={selectedPickListId}
              onValueChange={(v) => { setSelectedPickListId(v); runPlayback(v); }}
              onOpenChange={(o) => { if (o) loadPickLists(); }}
            >
              <SelectTrigger className="w-56 h-8 text-xs" data-testid="playback-select">
                <SelectValue placeholder={pickLists.length ? 'Pick an order…' : 'Load orders…'} />
              </SelectTrigger>
              <SelectContent>
                {pickLists.length === 0 && <SelectItem value="_none" disabled>No completed pick lists</SelectItem>}
                {pickLists.map((pl) => (
                  <SelectItem key={pl.id} value={pl.id}>
                    {pl.pick_list_number || pl.id.slice(-6)} · {(pl.items || []).length} items
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {playback && (
              <>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    if (playbackStep >= playback.route_points.length - 1) setPlaybackStep(0);
                    setPlaybackPlaying((p) => !p);
                  }}
                  data-testid="playback-play-btn"
                >
                  {playbackPlaying ? <><Pause size={12} className="mr-1" /> Pause</> : <><Play size={12} className="mr-1" weight="fill" /> Play</>}
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setPlayback(null); setSelectedPickListId(''); setPlaybackPlaying(false); setPlaybackStep(0); }}
                  data-testid="playback-clear-btn"
                >
                  <X size={12} />
                </Button>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  Step {Math.min(playbackStep + 1, playback.route_points.length)}/{playback.route_points.length} · {playback.total_distance_m} m
                </span>
              </>
            )}
            <Link
              to={selectedWh ? `/warehouse-layout/scenarios?wh=${selectedWh}` : '/warehouse-layout'}
              className="text-xs text-brand hover:underline flex items-center gap-1 ml-2"
              data-testid="scenarios-link"
            >
              <ArrowsSplit size={12} /> Scenarios
            </Link>
            <Link
              to={selectedWh ? `/warehouse-layout/optimizer?wh=${selectedWh}` : '/warehouse-layout'}
              className="text-xs text-brand hover:underline flex items-center gap-1"
              data-testid="optimizer-link"
            >
              <MagicWand size={12} /> Optimiser
            </Link>
            <Button
              size="sm"
              variant={kpiOpen ? 'default' : 'outline'}
              className={kpiOpen ? 'bg-brand hover:bg-brand/90 ml-2' : 'ml-2'}
              onClick={() => setKpiOpen((v) => !v)}
              disabled={!selectedWh}
              data-testid="toggle-kpis-btn"
              title="Labour hours, cost/order, picks/hr, shift performance"
            >
              <ChartLine size={12} className="mr-1" weight={kpiOpen ? 'fill' : 'regular'} /> KPIs
            </Button>
          </div>
        </div>

        {/* iter206: KPI panel */}
        {kpiOpen && (
          <WarehouseKpiPanel warehouseId={selectedWh} onClose={() => setKpiOpen(false)} />
        )}

        <div className="grid grid-cols-12 gap-4">
          {/* Palette */}
          <aside className="col-span-2 space-y-2" data-testid="tile-palette">
            <p className="overline">Palette</p>
            <p className="text-[10px] text-muted-foreground -mt-1">Drag onto grid</p>
            {kinds.map((k) => (
              <div
                key={k.kind}
                draggable
                onDragStart={onPaletteDragStart(k.kind)}
                onDragEnd={() => { setDragKind(null); setHoverCell(null); }}
                className="border border-border p-2 cursor-grab active:cursor-grabbing hover:border-brand/50 transition-colors flex items-center gap-2"
                style={{ background: `${k.color}12` }}
                data-testid={`palette-${k.kind}`}
              >
                <span className="inline-block w-4 h-4" style={{ background: k.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{k.label}</p>
                  {k.linkable && <p className="text-[9px] text-muted-foreground uppercase tracking-widest">Linkable</p>}
                </div>
                <DotsSix size={12} className="text-muted-foreground" />
              </div>
            ))}
          </aside>

          {/* Canvas */}
          <div className="col-span-7 border border-border bg-muted/30 overflow-auto" data-testid="layout-canvas-wrap" style={{ maxHeight: '75vh' }}>
            {loading ? (
              <p className="p-6 text-xs text-muted-foreground">Loading layout…</p>
            ) : (
              <svg
                ref={svgRef}
                width={width} height={height}
                onDragOver={onGridDragOver}
                onDrop={onGridDrop}
                onClick={() => setSelectedId(null)}
                data-testid="layout-svg"
              >
                {/* grid lines */}
                <defs>
                  <pattern id="grid" width={cell} height={cell} patternUnits="userSpaceOnUse">
                    <path d={`M ${cell} 0 L 0 0 0 ${cell}`} fill="none" stroke="#e2e8f0" strokeWidth="1" />
                  </pattern>
                  <pattern id="hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="6" stroke="#00000022" strokeWidth="2" />
                  </pattern>
                </defs>
                <rect width={width} height={height} fill="#ffffff" />
                <rect width={width} height={height} fill="url(#grid)" />
                {/* hover cell */}
                {hoverCell && dragKind && (() => {
                  const [w, h] = DEFAULT_SIZES[dragKind] || [3, 2];
                  const nx = Math.min(gridCols - w, hoverCell.x);
                  const ny = Math.min(gridRows - h, hoverCell.y);
                  const k = kindsById[dragKind];
                  return (
                    <rect
                      x={nx * cell} y={ny * cell}
                      width={w * cell} height={h * cell}
                      fill={k?.color || '#000'} opacity="0.35"
                      stroke={k?.color || '#000'} strokeDasharray="4 3"
                    />
                  );
                })()}
                {/* tiles */}
                {tiles.map((t) => {
                  const k = kindsById[t.kind];
                  if (!k) return null;
                  const isSelected = t.id === selectedId;
                  const isFloor = ['walkway', 'forklift', 'restricted'].includes(t.kind);
                  const overlayColor = overlayMode !== 'none' ? overlayTintFor(t) : null;
                  const baseFill = isFloor ? 'url(#hatch)' : k.color;
                  return (
                    <g key={t.id} data-testid={`tile-${t.id}`} onClick={(e) => { e.stopPropagation(); setSelectedId(t.id); }}>
                      <rect
                        x={t.x * cell} y={t.y * cell}
                        width={t.w * cell} height={t.h * cell}
                        fill={overlayColor || baseFill}
                        stroke={isSelected ? '#111827' : '#00000033'}
                        strokeWidth={isSelected ? 2 : 1}
                        opacity={overlayColor ? 0.9 : (isFloor ? 0.55 : 0.85)}
                        onMouseDown={onTileMouseDown(t, 'move')}
                        style={{ cursor: 'move' }}
                        data-overlay-tier={
                          overlayMode === 'abc' ? (abcData?.tiles?.[t.id]?.tier || '') :
                          overlayMode === 'congestion' ? (simData?.congestion?.[t.id]?.tier || '') : ''
                        }
                      />
                      <text
                        x={t.x * cell + (t.w * cell) / 2}
                        y={t.y * cell + (t.h * cell) / 2 + 4}
                        textAnchor="middle"
                        fontSize={Math.min(cell * 0.5, 14)}
                        fontWeight="bold"
                        fill={overlayColor ? '#ffffff' : (isFloor ? '#1f2937' : '#ffffff')}
                        pointerEvents="none"
                      >
                        {(t.label || t.base_code || k.short)
                          + (t.location_ids?.length ? ` · ${t.location_ids.length}` : '')}
                      </text>
                      {(t.levels > 1 || t.depth > 1) && t.h * cell >= 28 && (
                        <text
                          x={t.x * cell + (t.w * cell) / 2}
                          y={t.y * cell + (t.h * cell) / 2 + 4 + Math.min(cell * 0.45, 12)}
                          textAnchor="middle"
                          fontSize={Math.min(cell * 0.38, 10)}
                          fill={overlayColor ? '#ffffff' : (isFloor ? '#1f2937' : '#ffffffcc')}
                          pointerEvents="none"
                          data-testid={`tile-structure-${t.id}`}
                        >
                          {`${t.levels || 1}L×${t.depth || 1}D`}
                        </text>
                      )}
                      {isSelected && (
                        <rect
                          x={(t.x + t.w) * cell - 8}
                          y={(t.y + t.h) * cell - 8}
                          width={8} height={8}
                          fill="#111827"
                          stroke="#ffffff"
                          onMouseDown={onTileMouseDown(t, 'resize')}
                          style={{ cursor: 'nwse-resize' }}
                          data-testid={`resize-handle-${t.id}`}
                        />
                      )}
                    </g>
                  );
                })}

                {/* iter176 Phase 3: Playback route overlay */}
                {playback && (
                  <>
                    <polyline
                      points={playback.route_points.slice(0, Math.max(1, playbackStep + 1)).map((p) => `${p.x * cell},${p.y * cell}`).join(' ')}
                      fill="none"
                      stroke="#0ea5e9"
                      strokeWidth={3}
                      strokeDasharray="6 4"
                      strokeLinejoin="round"
                      opacity={0.85}
                      data-testid="playback-route"
                    />
                    {/* Waypoint dots */}
                    {playback.route_points.map((p, i) => (
                      <circle
                        key={i}
                        cx={p.x * cell} cy={p.y * cell}
                        r={i === 0 || i === playback.route_points.length - 1 ? 6 : 4}
                        fill={i === 0 ? '#22c55e' : i === playback.route_points.length - 1 ? '#dc2626' : '#0ea5e9'}
                        stroke="#ffffff" strokeWidth={2}
                        opacity={i <= playbackStep ? 1 : 0.35}
                      />
                    ))}
                    {/* Picker avatar at current step */}
                    {playbackStep < playback.route_points.length && (
                      <circle
                        cx={playback.route_points[playbackStep].x * cell}
                        cy={playback.route_points[playbackStep].y * cell}
                        r={9}
                        fill="#ffffff" stroke="#0ea5e9" strokeWidth={3}
                        data-testid="playback-picker"
                      >
                        <animate attributeName="r" values="9;12;9" dur="1s" repeatCount="indefinite" />
                      </circle>
                    )}
                  </>
                )}
              </svg>
            )}
          </div>

          {/* Inspector */}
          <aside className="col-span-3 space-y-3" data-testid="tile-inspector">
            {!selectedTile ? (
              <div className="border border-dashed border-border p-4 text-xs text-muted-foreground text-center">
                <ArrowsOutCardinal size={22} className="mx-auto mb-2 text-muted-foreground/60" />
                Drag a tile from the palette or click one on the grid to edit it.
              </div>
            ) : (
              <div className="border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="overline">Selected tile</p>
                  <Badge variant="outline">{kindsById[selectedTile.kind]?.label}</Badge>
                </div>
                <div>
                  <Label htmlFor="tile-label" className="text-xs">Label</Label>
                  <Input
                    id="tile-label"
                    value={selectedTile.label || ''}
                    onChange={(e) => updateSelected({ label: e.target.value })}
                    placeholder="e.g. Aisle A-01"
                    className="mt-1"
                    data-testid="tile-label-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumField label="X" value={selectedTile.x} onChange={(v) => updateSelected({ x: v })} max={gridCols - selectedTile.w} testid="tile-x" />
                  <NumField label="Y" value={selectedTile.y} onChange={(v) => updateSelected({ y: v })} max={gridRows - selectedTile.h} testid="tile-y" />
                  <NumField label="Width" value={selectedTile.w} onChange={(v) => updateSelected({ w: Math.max(1, v) })} max={gridCols - selectedTile.x} testid="tile-w" />
                  <NumField label="Height" value={selectedTile.h} onChange={(v) => updateSelected({ h: Math.max(1, v) })} max={gridRows - selectedTile.y} testid="tile-h" />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={rotateSelected} data-testid="rotate-btn">
                    <ArrowsClockwise size={12} className="mr-1" /> Rotate ({selectedTile.rotation}°)
                  </Button>
                  <Button size="sm" variant="outline" onClick={deleteSelected} className="text-red-700 border-red-200" data-testid="delete-tile-btn">
                    <Trash size={12} className="mr-1" /> Delete
                  </Button>
                </div>
                {kindsById[selectedTile.kind]?.linkable && (
                  <div className="pt-3 border-t border-border">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Zone</Label>
                        <Input value={selectedTile.zone || ''} onChange={(e) => updateSelected({ zone: e.target.value })} placeholder="e.g. A" className="mt-1 h-8 text-xs font-mono" data-testid="tile-zone-input" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Aisle</Label>
                        <Input value={selectedTile.aisle || ''} onChange={(e) => updateSelected({ aisle: e.target.value })} placeholder="e.g. 01" className="mt-1 h-8 text-xs font-mono" data-testid="tile-aisle-input" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">Linked locations</Label>
                      <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)} data-testid="link-locations-btn">
                        <LinkSimple size={12} className="mr-1" />
                        {selectedTile.location_ids?.length ? `${selectedTile.location_ids.length} linked` : 'Link'}
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Pick which real location codes live inside this tile, or set zone + aisle and use Auto-link racks.</p>
                    <RackStructurePanel
                      tile={selectedTile}
                      onChange={updateSelected}
                      onGenerate={handleGenerateRack}
                      onViewSkus={() => setContentsFor({ tile: selectedTile })}
                      generating={generating}
                    />
                  </div>
                )}
              </div>
            )}
            {tiles.length > 0 && (
              <div className="border border-border p-3 space-y-1">
                <p className="overline">Legend · Tile count</p>
                {Object.entries(tiles.reduce((m, t) => ({ ...m, [t.kind]: (m[t.kind] || 0) + 1 }), {})).map(([k, n]) => (
                  <div key={k} className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3" style={{ background: kindsById[k]?.color }} />
                      {kindsById[k]?.label}
                    </span>
                    <span className="font-mono">{n}</span>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>

      {selectedTile && linkOpen && (
        <LinkLocationsDialog
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
          warehouseId={selectedWh}
          tile={selectedTile}
          onSave={(ids) => { updateSelected({ location_ids: ids }); setLinkOpen(false); }}
        />
      )}
      {autoLink && (
        <AutoLinkDialog data={autoLink} onClose={() => setAutoLink(null)} onApply={applyAutoLink} />
      )}
      {contentsFor && (
        <LocationContentsDialog warehouseId={selectedWh} tile={contentsFor.tile || null} onClose={() => setContentsFor(null)} />
      )}
      {levelHeatOpen && <LevelHeatmapDialog warehouseId={selectedWh} onClose={() => setLevelHeatOpen(false)} />}
    </Layout>
  );
}

const NumField = ({ label, value, onChange, max, testid }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
    <Input
      type="number" min={0} max={max} value={value}
      onChange={(e) => onChange(Math.max(0, Math.min(max, Number(e.target.value) || 0)))}
      className="mt-1 h-8 text-xs font-mono"
      data-testid={testid}
    />
  </div>
);

// ------- Location linkage dialog -------
const LinkLocationsDialog = ({ open, onClose, warehouseId, tile, onSave }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set(tile.location_ids || []));

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await listLayoutLocations(warehouseId);
        setLocations(r.data.locations || []);
      } finally { setLoading(false); }
    })();
  }, [open, warehouseId]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return locations;
    return locations.filter((l) =>
      (l.location_code || '').toLowerCase().includes(needle) ||
      (l.zone || '').toLowerCase().includes(needle)
    );
  }, [locations, q]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="link-locations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={16} weight="fill" className="text-brand" />
            Link locations to “{tile.label || tile.kind}”
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Pick every real location code that physically sits inside this tile. Already-assigned locations show which tile owns them.
          </p>
        </DialogHeader>

        <Input placeholder="Search code or zone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="link-search" />
        <div className="border border-border max-h-96 overflow-y-auto text-xs" data-testid="link-list">
          {loading ? (
            <p className="p-4 text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-muted-foreground">
              <Warning size={14} className="inline mr-1" />
              No locations match.
            </p>
          ) : (
            filtered.map((l) => {
              const assignedElsewhere = l.assigned_to && l.assigned_to.tile_id !== tile.id;
              const isSelected = selected.has(l.id);
              return (
                <button
                  type="button"
                  key={l.id}
                  onClick={() => !assignedElsewhere && toggle(l.id)}
                  disabled={assignedElsewhere && !isSelected}
                  className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 flex items-center justify-between gap-2 ${isSelected ? 'bg-brand/10 border-l-2 border-l-brand' : ''} ${assignedElsewhere && !isSelected ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted/40'}`}
                  data-testid={`link-option-${l.location_code}`}
                >
                  <span className="font-mono font-bold">{l.location_code}</span>
                  <span className="text-muted-foreground">
                    {l.zone && <>Zone {l.zone}</>}
                    {assignedElsewhere && (
                      <span className="ml-2 text-amber-700">assigned → {l.assigned_to.tile_label}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <DialogFooter className="justify-between flex-row gap-2">
          <span className="text-xs text-muted-foreground self-center">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="link-cancel-btn"><X size={12} className="mr-1" /> Cancel</Button>
            <Button onClick={() => onSave([...selected])} className="bg-brand hover:bg-brand/90" data-testid="link-save-btn">
              <FloppyDisk size={12} className="mr-1" /> Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
